import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PURGE } from "redux-persist";
import apiInstance, { getMachineId } from '../../../api';
import moment from 'moment';
import ym from 'react-yandex-metrika';

// Async thunks
export const sendCodeToPhone = createAsyncThunk(
  'auth/sendCodeToPhone',
  async (data, { rejectWithValue }) => {
    try {
      const api = '/auth/sendCodeToPhone?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        localStorage.setItem('session', response.data.data.session);
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTokenBySession = createAsyncThunk(
  'auth/createTokenBySession',
  async (data, { rejectWithValue }) => {
    try {
      const api = '/auth/createTokenBySession?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        localStorage.setItem('token', response.data.data.token);
        ym('reachGoal','user_authorization');
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const api = '/auth/logout?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        // Очищаем localStorage и очищаем persisted store
        localStorage.removeItem('token');
        localStorage.removeItem('session');

        ym('reachGoal','user_logout');
        // Просим redux-persist очистить хранилище (если используется)
        try { dispatch({ type: PURGE }); } catch (e) { /* noop */ }

        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      // Несмотря на ошибку на сервере, выполняем клиентскую очистку
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('session');
        dispatch({ type: PURGE });
      } catch (e) { /* noop */ }

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const giveRoleToNewUser = createAsyncThunk(
  'auth/giveRoleToNewUser',
  async (data, { rejectWithValue }) => {
    try {
      const api = '/access/roleUsers/createDefaultRole?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async (data, { rejectWithValue }) => {
    try {
      const api = '/user/getProfile?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const generateAuthCode = createAsyncThunk(
  'auth/generateAuthCode',
  async (phone, { rejectWithValue }) => {
    try {
      const api = '/auth/tools/generateCodeWithValue?XDEBUG_SESSION_START=MyVSCode';
      const response = await apiInstance.post(api, JSON.stringify({ data: { phone, device: getMachineId() } }));
      
      if (response.data.result) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error('Ошибка при выполнении запроса:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  isAuth: !!localStorage.getItem('token') || false,

  //Code To Phone
  sendCodeStatus: null,
  sendCodeError: null,

  //Token By Session
  sendCodeTime: null,

  //Token By Session
  createTokenStatus: null,
  createTokenError: null,

  //Logout
  logoutStatus: null,

  //Give Role To New User
  isNewUser: false,
  giveRoleToNewUserRequestStatus: null,

  //User Profile
  userProfileRequestStatus: null,
  userProfile: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => {
        return initialState;
      })
      
      // Send Code To Phone
      .addCase(sendCodeToPhone.pending, (state) => {
        state.sendCodeStatus = 'pending';
        state.sendCodeError = null;
      })
      .addCase(sendCodeToPhone.fulfilled, (state) => {
        state.sendCodeStatus = 'resolved';
        state.sendCodeTime = moment().unix();
        state.sendCodeError = null;
      })
      .addCase(sendCodeToPhone.rejected, (state, action) => {
        state.sendCodeStatus = 'rejected';
        state.sendCodeError = action.payload || action.error?.message || 'Ошибка отправки кода';
      })
      
      // Create Token By Session
      .addCase(createTokenBySession.pending, (state) => {
        state.createTokenStatus = 'pending';
        state.createTokenError = null;
      })
      .addCase(createTokenBySession.fulfilled, (state, action) => {
        state.createTokenStatus = 'resolved';
        state.isAuth = true;
        state.isNewUser = action.payload.isNewUser;
        state.createTokenError = null;
      })
      .addCase(createTokenBySession.rejected, (state, action) => {
        state.createTokenStatus = 'rejected';
        state.createTokenError = action.payload || action.error?.message || 'Ошибка верификации кода';
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.logoutStatus = 'pending';
      })
      .addCase(logout.fulfilled, (state) => {
        // Полная очистка состояния при успешном logout
        return {
          ...initialState,
          logoutStatus: 'resolved',
          isAuth: false,
        };
      })
      .addCase(logout.rejected, (state) => {
        // Даже при ошибке — гарантируем клиентскую очистку состояния
        return {
          ...initialState,
          logoutStatus: 'rejected',
          isAuth: false,
        };
      })
      
      // Give Role To New User
      .addCase(giveRoleToNewUser.pending, (state) => {
        state.giveRoleToNewUserRequestStatus = 'pending';
      })
      .addCase(giveRoleToNewUser.fulfilled, (state) => {
        state.giveRoleToNewUserRequestStatus = 'resolved';
        state.isNewUser = false;
      })
      .addCase(giveRoleToNewUser.rejected, (state) => {
        state.giveRoleToNewUserRequestStatus = 'rejected';
      })
      
      // Get User Profile
      .addCase(getUserProfile.pending, (state) => {
        state.userProfileRequestStatus = 'pending';
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.userProfileRequestStatus = 'resolved';
        state.userProfile = action.payload;
      })
      .addCase(getUserProfile.rejected, (state) => {
        state.userProfileRequestStatus = 'rejected';
      })

      // Generate Auth Code
      .addCase(generateAuthCode.pending, (state) => {
        state.generateAuthCodeStatus = 'pending';
      })
      .addCase(generateAuthCode.fulfilled, (state) => {
        state.generateAuthCodeStatus = 'resolved';
      })
      .addCase(generateAuthCode.rejected, (state) => {
        state.generateAuthCodeStatus = 'rejected';
      });
  },
  reducers: {
    setAuth: (state, action) => { 
      state.isAuth = action.payload; 
    },
    
    // Полная очистка состояния (для принудительного logout)
    clearAllState: () => {
      return initialState;
    },
    
    // Clear status reducers
    sendCodeToPhoneStatusClear: (state) => { 
      state.sendCodeStatus = null; 
    },
    clearCreateTokenStatus: (state) => { 
      state.createTokenStatus = null; 
    },
    clearLogoutStatus: (state) => { 
      state.logoutStatus = null; 
    },
    clearGiveRoleToNewUserStatus: (state) => { 
      state.giveRoleToNewUserRequestStatus = null; 
    },
    clearUserProfileStatus: (state) => { 
      state.userProfileRequestStatus = null; 
    },
    
    // Direct setters
    setUserProfile: (state, action) => { 
      state.userProfile = action.payload; 
    },
  },
});

export const {
  setAuth,
  clearAllState,
  sendCodeToPhoneStatusClear,
  clearCreateTokenStatus,
  clearLogoutStatus,
  clearGiveRoleToNewUserStatus,
  clearUserProfileStatus,
  setUserProfile,
} = authSlice.actions;

export default authSlice.reducer;
